import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface NotionInstallation {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  workspaceIcon: string | null;
  botId: string;
  organizationId: string;
  isActive: boolean;
  suspendedAt: string | null;
  installedAt: string;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface NotionState {
  currentInstallation: NotionInstallation | null;
  installations: NotionInstallation[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: NotionState = {
  currentInstallation: null,
  installations: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getNotionInstallations = createAsyncThunk(
  'notion/getInstallations',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/notion/installations?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getNotionInstallation = createAsyncThunk(
  'notion/getInstallation',
  async (installationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/notion/installations/${installationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const disconnectNotion = createAsyncThunk(
  'notion/disconnectNotion',
  async (installationId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/notion/installations/${installationId}`, { method: 'DELETE' });
      return installationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get installations
  [getNotionInstallations.pending.type]: { status: 'loading' as const },
  [getNotionInstallations.fulfilled.type]: (state: NotionState, action: any) => {
    if (!action.payload) {
      state.installations = [];
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    const installations = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.installations || action.payload.installations || [];

    state.installations = installations;
    state.currentInstallation = installations[0] || null;
    state.status = 'success';
    state.error = '';
  },
  [getNotionInstallations.rejected.type]: (state: NotionState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installations';
  },

  // Get single installation
  [getNotionInstallation.pending.type]: { status: 'loading' as const },
  [getNotionInstallation.fulfilled.type]: (state: NotionState, action: any) => {
    if (!action.payload) {
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    state.currentInstallation = action.payload.response || action.payload;
    state.status = 'success';
    state.error = '';
  },
  [getNotionInstallation.rejected.type]: (state: NotionState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installation';
  },

  // Disconnect Notion
  [disconnectNotion.pending.type]: { status: 'loading' as const },
  [disconnectNotion.fulfilled.type]: (state: NotionState) => {
    state.currentInstallation = null;
    state.installations = [];
    state.status = 'success';
    state.error = '';
  },
  [disconnectNotion.rejected.type]: (state: NotionState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to disconnect Notion';
  },
};

// ==============================|| SLICE ||============================== //

export const notionSlice = createSlice({
  name: 'notion',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentInstallation(state, action) {
      state.currentInstallation = action.payload;
    },
    clearNotionState(state) {
      state.currentInstallation = null;
      state.installations = [];
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentInstallation, clearNotionState } = notionSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectNotionInstallations = (state: any) => state.notion.installations;
export const selectCurrentNotionInstallation = (state: any) => state.notion.currentInstallation;
export const selectNotionStatus = (state: any) => state.notion.status;
export const selectNotionError = (state: any) => state.notion.error;

// ==============================|| HELPER ||============================== //

export const initiateNotionConnect = createAsyncThunk(
  'notion/initiateConnect',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/notion/install?organizationId=${organizationId}`);
      const installUrl = response.response?.installUrl || response.installUrl;

      if (!installUrl) {
        throw new Error('No installation URL returned from API');
      }

      window.location.href = installUrl;

      return { installUrl };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export default notionSlice.reducer;
